import { Box, Stack, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { type Notification } from "@src/appV2/lib/Braze/useBrazeNotifications";
import { useOpenUrl } from "@src/mobile/utils/openUrl";

import { IconButton } from "../IconButton";
import { Text } from "../Text";
import { resolveNotificationCardUrl } from "./resolveNotificationCardUrl";

export interface NotificationCardProps {
  notification: Notification;
  onClick?: (notification: Notification) => void;
  onDismiss?: (notification: Notification) => void;
  sx?: SxProps<Theme>;
}

export function NotificationCard(props: NotificationCardProps) {
  const { notification, onClick, onDismiss, sx } = props;
  const openUrl = useOpenUrl();

  function handleClick() {
    if (notification.url) {
      const resolvedUrl = resolveNotificationCardUrl(notification.url);
      void openUrl(resolvedUrl);
    }
    onClick?.(notification);
  }

  function handleDismiss(event: React.MouseEvent) {
    event.stopPropagation();
    onDismiss?.(notification);
  }

  const hasLink = Boolean(notification.url);

  return (
    <Box
      component={hasLink ? "button" : "div"}
      sx={mergeSxProps(
        (theme: Theme) => ({
          width: "100%",
          display: "block",
          textAlign: "left",
          backgroundColor: notification.isViewed
            ? theme.palette.background.standout
            : theme.palette.background.base,
          borderRadius: theme.borderRadius?.large,
          border: `${theme.borderWidth?.regular} solid ${theme.palette.border?.base}`,
          boxShadow: theme.shadow?.card,
          position: "relative",
          transition: "background-color 0.2s ease",
          ...(hasLink && {
            cursor: "pointer",
            "&:hover": {
              backgroundColor: theme.palette.background.muted,
            },
          }),
        }),
        sx
      )}
      onClick={hasLink ? handleClick : undefined}
    >
      {!notification.isViewed && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: theme.spacing(4),
            left: theme.spacing(3),
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: theme.palette.primary.main,
          })}
        />
      )}

      <Stack
        spacing={2}
        sx={(theme) => ({
          padding: theme.spacing(4),
          paddingLeft: notification.isViewed ? theme.spacing(4) : theme.spacing(5),
        })}
      >
        {notification.title && (
          <Text
            bold
            variant="body1"
            sx={(theme) => ({
              color: theme.palette.text.primary,
              wordBreak: "break-word",
            })}
          >
            {notification.title}
          </Text>
        )}

        {notification.description && (
          <Text
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              wordBreak: "break-word",
            })}
          >
            {notification.description}
          </Text>
        )}

        {notification.linkText && hasLink && (
          <Text
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.link?.base,
            })}
          >
            {notification.linkText}
          </Text>
        )}
      </Stack>

      {onDismiss && (
        <IconButton
          iconType="close"
          variant="outlined"
          size="small"
          sx={(theme) => ({
            position: "absolute",
            top: theme.spacing(2),
            right: theme.spacing(2),
            padding: theme.spacing(2),
            borderRadius: "50%",
            opacity: 0.6,
          })}
          onClick={(event) => {
            handleDismiss(event);
          }}
        />
      )}
    </Box>
  );
}
